import React from 'react';
import { Link } from 'react-router-dom';
import {ArrowRight, CheckCircle, Award, Clock, Shield, Users} from 'lucide-react';

export default function HomePage() {
  const services = [
    { id: 'dot-specimen', name: 'DOT & Non-DOT Specimen Collection', icon: '🧪' },
    { id: 'dna-testing', name: 'DNA Testing', icon: '🧬' },
    { id: 'blood-testing', name: 'Blood Testing', icon: '💉' },
    { id: 'urine-drug-testing', name: 'Urine Drug Testing', icon: '🔬' },
    { id: 'flu-testing', name: 'Flu Testing', icon: '🦠' },
    { id: 'fingerprint', name: 'Electronic Fingerprint', icon: '👆' },
    { id: 'i9-checks', name: 'I-9 Background Checks', icon: '📋' },
    { id: 'ctpa', name: 'C/TPA Services', icon: '🏥' },
  ];

  const features = [
    { icon: Award, title: 'Certified & Accredited', description: 'Fully certified laboratory meeting the highest industry standards' },
    { icon: Clock, title: 'Fast Results', description: 'Quick turnaround times without compromising accuracy' },
    { icon: Shield, title: 'Confidential & Secure', description: 'Your privacy and data security are our top priorities' },
    { icon: Users, title: 'Expert Staff', description: 'Experienced professionals dedicated to quality care' },
  ];

  const testimonials = [
    { name: 'Sarah Johnson', text: 'Professional service and quick results. The staff made me feel comfortable throughout the entire process.', rating: 5 },
    { name: 'Michael Chen', text: 'Excellent facility with knowledgeable staff. They explained everything clearly and answered all my questions.', rating: 5 },
    { name: 'Emily Rodriguez', text: 'Very impressed with the efficiency and professionalism. Highly recommend EmergeNC Diagnostics!', rating: 5 },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-teal-600 text-white py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.pexels.com/photos/3825527/pexels-photo-3825527.jpeg?auto=compress&cs=tinysrgb&w=1920" 
            alt="Diverse medical laboratory professionals performing diagnostic testing"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional Laboratory Services You Can Trust
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-50">
              Accurate diagnostics, reliable results, and compassionate care for all your testing needs.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/appointment"
                className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-all hover:shadow-lg"
              >
                Book Appointment <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-800 transition-all"
              >
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Image Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg?auto=compress&cs=tinysrgb&w=1200" 
                alt="Diverse medical laboratory technician conducting blood specimen collection and analysis"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">State-of-the-Art Laboratory Services</h2>
              <p className="text-lg text-gray-600 mb-6">
                At EmergeNC Diagnostics, we combine cutting-edge technology with compassionate care to deliver accurate, reliable results. Our certified professionals are dedicated to providing exceptional service for all your diagnostic needs.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#4AB8D8] flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Comprehensive testing services for individuals and businesses</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#4AB8D8] flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Fast turnaround times with accurate results</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#4AB8D8] flex-shrink-0 mt-1" />
                  <span className="text-gray-700">HIPAA-compliant and fully certified facility</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-blue-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive laboratory services tailored to meet your diagnostic needs
            </p>
          </div>
          <div className="mb-12">
            <img 
              src="https://images.pexels.com/photos/5726794/pexels-photo-5726794.jpeg?auto=compress&cs=tinysrgb&w=1200" 
              alt="African American female laboratory scientist analyzing test samples in modern diagnostic facility"
              className="rounded-2xl shadow-xl w-full h-80 object-cover"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.id}`}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-200"
              >
                <div className="text-4xl mb-4" aria-hidden="true">{service.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.name}</h3>
                <div className="flex items-center text-blue-600 font-medium">
                  Learn more <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              View All Services <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Staff Excellence Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#4AB8D8] to-[#14B8A6] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Expert Care, Every Time</h2>
              <p className="text-lg text-blue-50 mb-6">
                Our team of certified laboratory professionals brings years of experience and dedication to every test we perform. We're committed to accuracy, efficiency, and compassionate patient care.
              </p>
              <Link
                to="/appointment"
                className="inline-flex items-center gap-2 bg-white text-[#4AB8D8] px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-all"
              >
                Schedule Your Visit <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div>
              <img 
                src="https://images.pexels.com/photos/8460157/pexels-photo-8460157.jpeg?auto=compress&cs=tinysrgb&w=1200" 
                alt="Hispanic male laboratory technician using microscope for medical testing and diagnostics"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">What Our Patients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl" aria-hidden="true">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <p className="font-semibold text-gray-900">- {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Advanced Diagnostic Technology</h2>
              <p className="text-lg text-gray-600 mb-6">
                We utilize the latest laboratory equipment and testing methodologies to ensure precise results. From DNA analysis to drug screening, our facility is equipped to handle a wide range of diagnostic services with the highest standards of quality control.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#4AB8D8]"></div>
                  <span className="text-gray-700">DOT and Non-DOT qualified collection site</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#4AB8D8]"></div>
                  <span className="text-gray-700">Electronic fingerprinting and background check services</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#4AB8D8]"></div>
                  <span className="text-gray-700">Comprehensive drug testing panels available</span>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img 
                src="https://images.pexels.com/photos/3938023/pexels-photo-3938023.jpeg?auto=compress&cs=tinysrgb&w=1200" 
                alt="Modern medical laboratory equipment for blood analysis and diagnostic testing"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-blue-50">
            Schedule your appointment today and experience professional laboratory services with care and precision.
          </p>
          <Link
            to="/appointment"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-all hover:shadow-lg text-lg"
          >
            Book Your Appointment <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
      </section>

      {/* Compliance Badges */}
      <section className="py-12 px-4 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-center text-gray-600 font-semibold mb-6">Certified & Compliant</h3>
          <div className="flex flex-wrap justify-center items-center gap-8">
            <div className="flex items-center gap-2 text-gray-700">
              <CheckCircle className="w-6 h-6 text-green-600" aria-hidden="true" />
              <span className="font-medium">CLIA Certified</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <CheckCircle className="w-6 h-6 text-green-600" aria-hidden="true" />
              <span className="font-medium">CAP Accredited</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <CheckCircle className="w-6 h-6 text-green-600" aria-hidden="true" />
              <span className="font-medium">HIPAA Compliant</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <CheckCircle className="w-6 h-6 text-green-600" aria-hidden="true" />
              <span className="font-medium">DOT Qualified</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
